import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api from "@/lib/api";
import { waLink } from "@/lib/site";

export const EnquiryDialog = ({ open, onOpenChange, pkg }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", travel_date: "", travelers: 2, message: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        travelers: Number(form.travelers),
        package_slug: pkg?.slug,
        package_title: pkg?.title,
      });
      toast.success("Enquiry sent! Opening WhatsApp to connect you instantly...");
      onOpenChange(false);
      const msg = `Hi click2trip! I'm ${form.name}. I'm interested in "${pkg?.title || "a custom trip"}" for ${form.travelers} traveler(s)${form.travel_date ? ` around ${form.travel_date}` : ""}. Please share details.`;
      window.open(waLink(msg), "_blank");
      setForm({ name: "", phone: "", email: "", travel_date: "", travelers: 2, message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-bone border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sun/50 focus:border-sun transition-all";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">
            Enquire {pkg ? `— ${pkg.title}` : "Now"}
          </DialogTitle>
          <p className="text-sm text-ink/60">Fill this in and continue on WhatsApp for instant confirmation.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5 mt-2" data-testid="enquiry-form">
          <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={input} data-testid="enquiry-name-input" />
          <input required placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} className={input} data-testid="enquiry-phone-input" />
          <input type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} className={input} data-testid="enquiry-email-input" />
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={form.travel_date} onChange={set("travel_date")} className={`${input} text-ink/70`} data-testid="enquiry-date-input" />
            <select value={form.travelers} onChange={set("travelers")} className={input} data-testid="enquiry-travelers-select">
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} traveler{n > 1 ? "s" : ""}</option>)}
              <option value="7">7+ travelers</option>
            </select>
          </div>
          <textarea placeholder="Anything we should know? (optional)" rows={3} value={form.message} onChange={set("message")}
            className={`${input} resize-none`} data-testid="enquiry-message-input" />
          <button type="submit" disabled={loading} data-testid="enquiry-submit-btn"
            className="w-full bg-sun text-bone font-semibold py-3.5 rounded-full hover:bg-[#e85a28] hover:scale-[1.01] active:scale-[0.98] transition-all disabled:opacity-60">
            {loading ? "Sending..." : "Send & Continue on WhatsApp"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
